import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Download, TrendingUp, Eye, MousePointer, Mail, Users } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const Card = ({ title, right, children }) => (
  <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const MetricCard = ({ icon: Icon, title, value, change, changeType }) => (
  <div className="bg-white rounded-2xl p-4 border border-gray-100">
    <div className="flex items-center justify-between">
      <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white flex items-center justify-center">
        <Icon size={20} />
      </div>
      <span className={`text-sm font-medium ${
        changeType === 'positive' ? 'text-green-600' : 'text-red-600'
      }`}>
        {change}
      </span>
    </div>
    <div className="mt-3">
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm text-gray-500">{title}</div>
    </div>
  </div>
);

function AnalyticsView() {
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');
  
  const metrics = [
    { icon: Eye, title: "Total Views", value: "24,847", change: "+15.3%", changeType: "positive" },
    { icon: Mail, title: "Email Opens", value: "18,234", change: "+8.7%", changeType: "positive" },
    { icon: MousePointer, title: "Link Clicks", value: "3,456", change: "+23.1%", changeType: "positive" },
    { icon: Users, title: "Responses", value: "567", change: "-2.1%", changeType: "negative" },
  ];

  const campaignData = [
    { 
      name: "Book Launch: Coloring Odyssey", 
      opens: 1247, 
      clicks: 234, 
      responses: 15, 
      outlets: 45,
      status: "Completed",
      openRate: "68.2%"
    },
    { 
      name: "Artist Single: HXZE – Nova", 
      opens: 892, 
      clicks: 156, 
      responses: 8, 
      outlets: 32,
      status: "Active",
      openRate: "72.1%"
    },
    { 
      name: "App Update: StoryScape XR", 
      opens: 654, 
      clicks: 98, 
      responses: 12, 
      outlets: 28,
      status: "Scheduled",
      openRate: "61.4%"
    },
  ];

  const topPerformers = [
    { outlet: "WRFG", type: "Radio", opens: 124, clicks: 23, responses: 5, engagement: "18.5%" },
    { outlet: "Pitchfork", type: "Blog", opens: 301, clicks: 77, responses: 9, engagement: "25.6%" },
    { outlet: "IssueWire", type: "PR", opens: 840, clicks: 110, responses: 12, engagement: "13.1%" },
    { outlet: "TechCrunch", type: "Blog", opens: 456, clicks: 89, responses: 7, engagement: "19.5%" },
  ];

  const timeframes = [
    { label: "7 Days", value: "7d" },
    { label: "30 Days", value: "30d" },
    { label: "90 Days", value: "90d" },
    { label: "1 Year", value: "1y" },
  ];

  return (
    <div className="space-y-6">
      {/* Header with Timeframe Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">PR Analytics Dashboard</h2>
        <div className="flex items-center gap-2">
          {timeframes?.map((timeframe) => (
            <button
              key={timeframe?.value}
              onClick={() => setSelectedTimeframe(timeframe?.value)}
              className={`px-3 py-1 rounded-lg text-sm transition ${
                selectedTimeframe === timeframe?.value
                  ? 'bg-black text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {timeframe?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics?.map((metric, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <MetricCard {...metric} />
          </motion.div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Engagement Chart */}
          <Card title="Engagement Overview" right={
            <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
              <TrendingUp size={14} />
              View Details
            </button>
          }>
            <div className="h-64 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl flex items-center justify-center border border-gray-100">
              <div className="text-center text-gray-500">
                <TrendingUp size={48} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Interactive engagement chart</p>
                <p className="text-xs">Shows opens, clicks, and responses over time</p>
              </div>
            </div>
          </Card>

          {/* Campaign Performance */}
          <Card title="Recent Campaigns">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="py-3 font-medium">Campaign</th>
                    <th className="py-3 font-medium">Opens</th>
                    <th className="py-3 font-medium">Clicks</th>
                    <th className="py-3 font-medium">Responses</th>
                    <th className="py-3 font-medium">Open Rate</th>
                    <th className="py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignData?.map((campaign, index) => (
                    <motion.tr 
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-b border-gray-50 hover:bg-gray-50 transition"
                    >
                      <td className="py-3">
                        <div>
                          <p className="font-medium">{campaign?.name}</p>
                          <p className="text-xs text-gray-500">{campaign?.outlets} outlets</p>
                        </div>
                      </td>
                      <td className="py-3 font-medium">{campaign?.opens?.toLocaleString()}</td>
                      <td className="py-3 font-medium">{campaign?.clicks}</td>
                      <td className="py-3 font-medium">{campaign?.responses}</td>
                      <td className="py-3">
                        <span className="font-medium text-green-600">{campaign?.openRate}</span>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          campaign?.status === 'Completed' ? 'bg-green-100 text-green-700' :
                          campaign?.status === 'Active'? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {campaign?.status}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Top Performing Outlets */}
          <Card title="Top Performing Outlets">
            <div className="space-y-3">
              {topPerformers?.map((performer, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-green-400 to-blue-500 text-white flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{performer?.outlet}</p>
                      <p className="text-xs text-gray-500">{performer?.type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">{performer?.engagement}</p>
                    <p className="text-xs text-gray-500">engagement rate</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          {/* Delivery Health */}
          <Card title="Delivery Health">
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <CheckCircle2 size={16} className="text-green-600"/>
                <div>
                  <p className="font-medium">DKIM Aligned</p>
                  <p className="text-xs text-gray-500">Email authentication verified</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle2 size={16} className="text-green-600"/>
                <div>
                  <p className="font-medium">SPF Valid</p>
                  <p className="text-xs text-gray-500">Sender policy framework active</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <AlertCircle size={16} className="text-amber-600"/>
                <div>
                  <p className="font-medium">Sender Reputation</p>
                  <p className="text-xs text-gray-500">Consider warming up new domains</p>
                </div>
              </li>
            </ul>
          </Card>

          {/* Quick Stats */}
          <Card title="Quick Insights">
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp size={14} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Best Performing Day</span>
                </div>
                <p className="text-xs text-blue-600">Tuesday shows 23% higher open rates</p>
              </div>
              
              <div className="p-3 bg-green-50 rounded-xl border border-green-100">
                <div className="flex items-center gap-2 mb-1">
                  <Users size={14} className="text-green-600" />
                  <span className="text-sm font-medium text-green-800">Top Audience</span>
                </div>
                <p className="text-xs text-green-600">Tech journalists show highest engagement</p>
              </div>

              <div className="p-3 bg-purple-50 rounded-xl border border-purple-100">
                <div className="flex items-center gap-2 mb-1">
                  <Eye size={14} className="text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">Peak Hours</span>
                </div>
                <p className="text-xs text-purple-600">10 AM - 12 PM EST optimal send time</p>
              </div>
            </div>
          </Card>

          {/* Export Options */}
          <Card title="Export Reports">
            <div className="grid gap-2">
              <button className="flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                <Download size={14} />
                PDF Summary
              </button>
              <button className="flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                <Download size={14} />
                CSV (Raw Events)
              </button>
              <button className="flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                <Download size={14} />
                Excel Report
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsView;